import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import {
  saveTableColumnsPreferences,
  saveWalletTableColumnsPreferences,
} from 'src/services/wallet-monitor';
import { TableColumnsPreference } from 'src/types/wallet-monitor';
import * as Yup from 'yup';

type SaveTableColumnsPreferencesModalProps = {
  open: boolean;
  handleClose: (changed: boolean) => void;
  filterData: any;
  walletId: number;
  setSelectedPreference?: (preference?: TableColumnsPreference) => void;
};

export function SaveTableColumnsPreferencesModal({
  open,
  handleClose,
  filterData,
  walletId,
  setSelectedPreference,
}: SaveTableColumnsPreferencesModalProps) {
  const queryClient = useQueryClient();
  const saveTableColumnsPreferencesMutation = useMutation(
    saveTableColumnsPreferences,
    {
      onSuccess: async (response, variables) => {
        if (variables.applyToWallet && response?.id) {
          const walletPreference = await saveWalletTableColumnsPreferences({
            walletId,
            preferenceId: response.id,
          });
          setSelectedPreference?.(walletPreference?.preference);
        }
        queryClient.invalidateQueries([
          'wallet-table-preferences-list',
          walletId,
        ]);
        handleClose(true);
      },
    },
  );

  const ValidateForm = Yup.object().shape({
    title: Yup.string().required('Campo obrigatório'),
  });

  const formik = useFormik({
    initialValues: {
      title: '',
      applyToWallet: true,
    },
    validationSchema: ValidateForm,
    onSubmit: (values) => {
      saveTableColumnsPreferencesMutation.mutate({
        title: values.title,
        data: {
          ...filterData,
        },
        applyToWallet: values.applyToWallet,
      });
    },
  });
  return (
    <Dialog
      open={open}
      maxWidth="lg"
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}>
      <DialogContent
        sx={(theme) => ({
          padding: '20px 48px 48px',
          width: '800px',
          [theme.breakpoints.down('lg')]: {
            width: 'auto',
          },
        })}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              margin: '0 -32px',
            }}>
            <IconButton
              aria-label="fechar"
              color="inherit"
              size="small"
              onClick={() => handleClose(false)}>
              <Close
                fontSize="small"
                sx={{
                  color: 'black',
                }}
              />
            </IconButton>
          </Box>
          <Box display="flex" justifyContent="center" padding={2}>
            <Typography variant="h5" fontSize={18}>
              Salvar preferências de colunas
            </Typography>
          </Box>
          <Grid container spacing={2} gap={2} paddingTop={2}>
            <Grid item xs={12}>
              <Typography variant="body1" fontSize={14}>
                Nome das preferências*
              </Typography>
              <FormControl fullWidth variant="outlined">
                <TextField
                  name="title"
                  placeholder="Dê um nome para esse conjunto de parâmetros que será salvo nas suas preferências de colunas"
                  onChange={formik.handleChange}
                  inputProps={{ maxLength: 50 }}
                  value={formik.values.title}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="applyToWallet"
                    checked={formik.values.applyToWallet}
                    onChange={formik.handleChange}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontSize={14}>
                    Aplicar esta preferência na carteira atual
                  </Typography>
                }
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            xs={12}
            gap={4}
            sx={{
              justifyContent: 'center',
              marginTop: '50px',
            }}>
            <Grid item>
              <Button
                variant="contained"
                type="submit"
                disabled={saveTableColumnsPreferencesMutation.isLoading}>
                {saveTableColumnsPreferencesMutation.isLoading ? (
                  <CircularProgress sx={{ color: '#FFF' }} />
                ) : (
                  'Salvar'
                )}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                type="reset"
                onClick={() => handleClose(false)}
                disabled={saveTableColumnsPreferencesMutation.isLoading}>
                Cancelar
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}
